/**
 * SubirInformativa — sube KML/KMZ/GeoJSON (líneas/puntos/polígonos) y crea una
 * CAPA INFORMATIVA de referencia (overlay en el mapa, NO operativa, sin proxy a
 * parcelas) vía POST /v1/capas/informativa → capa_insert_informativa (mig 0093).
 *
 * Discrimina al subir: nombre + subtipo (presets + "Otro…" libre) + estilo
 * (color/grosor/opacidad). Conserva cualquier geometría (parseLayerFileFeatures).
 */
import { useMemo, useState } from 'react';
import { bffFetch, invalidateCache } from '@aosenuma/lib-supabase';
import { showToast } from '@aosenuma/ui';
import { parseLayerFileFeatures, type CapaFeatureItem } from '../../captura/parseLayerFile';

const SUBTIPO_PRESETS = [
  'Transmisión eléctrica',
  'Ducto / hidrocarburos',
  'Agua',
  'Vía / camino',
  'Servidumbre',
  'Área restringida',
] as const;
const OTRO = '__otro__';

const COLORES = ['#FBBF24', '#F97316', '#A855F7', '#4A9CA5', '#FB7185', '#3a9c7e', '#6394c0', '#F2F4ED'];

interface InformativaResult {
  idempotent_hit: boolean;
  capa_id: string;
  total: number;
  created: number;
  failed: number;
}

function formatoFromName(name: string): 'kml' | 'kmz' | 'geojson' {
  const n = name.toLowerCase();
  if (n.endsWith('.kmz')) return 'kmz';
  if (n.endsWith('.kml')) return 'kml';
  return 'geojson';
}

export function SubirInformativa({ onCreated }: { onCreated?: () => void }) {
  const [fileName, setFileName] = useState('');
  const [nombre, setNombre] = useState('');
  const [subtipoSel, setSubtipoSel] = useState<string>(SUBTIPO_PRESETS[0]);
  const [subtipoOtro, setSubtipoOtro] = useState('');
  const [color, setColor] = useState(COLORES[0]);
  const [grosor, setGrosor] = useState(2);
  const [opacidad, setOpacidad] = useState(0.6);
  const [relleno, setRelleno] = useState(true);
  const [items, setItems] = useState<CapaFeatureItem[]>([]);
  const [parseWarnings, setParseWarnings] = useState<string[]>([]);
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<InformativaResult | null>(null);

  const conteoPorTipo = useMemo(() => {
    const m: Record<string, number> = {};
    for (const it of items) m[it.geometry_type] = (m[it.geometry_type] ?? 0) + 1;
    return m;
  }, [items]);

  const subtipo = subtipoSel === OTRO ? subtipoOtro.trim() : subtipoSel;

  async function handleFile(file: File | undefined) {
    if (!file) return;
    setParsing(true);
    setResult(null);
    const res = await parseLayerFileFeatures(file);
    setParsing(false);
    if (!res.ok) {
      setItems([]);
      setFileName('');
      setParseWarnings([]);
      showToast(res.error, { variant: 'error' });
      return;
    }
    setFileName(file.name);
    if (!nombre.trim()) setNombre(file.name.replace(/\.[^.]+$/, ''));
    setParseWarnings(res.warnings);
    setItems(res.items);
  }

  async function submit() {
    if (!nombre.trim()) { showToast('Ponle un nombre a la capa.', { variant: 'error' }); return; }
    if (items.length === 0) { showToast('Sube un archivo con geometrías.', { variant: 'error' }); return; }
    const features = items.map((it) => ({
      geojson_geom: it.geojson_geom,
      nombre: it.nombre,
      properties: it.properties ?? {},
    }));
    const submissionKey = crypto.randomUUID();
    setSubmitting(true);
    try {
      const res = await bffFetch<InformativaResult>('/v1/capas/informativa', {
        method: 'POST',
        body: {
          nombre: nombre.trim(),
          subtipo: subtipo || null,
          estilo: { color, grosor, opacidad, relleno },
          archivo: fileName || null,
          formato: fileName ? formatoFromName(fileName) : null,
          features,
          idempotency_key: submissionKey,
        },
      });
      setResult(res);
      invalidateCache(['capas_registry', 'capas_informativas']);
      if (res.failed > 0) {
        showToast(`${res.created} features creadas, ${res.failed} con error.`, { variant: 'error' });
      } else {
        showToast(`Capa "${nombre.trim()}" creada con ${res.created} feature(s).`, { variant: 'success' });
        onCreated?.();
      }
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Error al crear la capa.', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <p className="panel-sub" style={{ marginTop: 0 }}>
        Capa de <strong>referencia</strong> (líneas de transmisión, ductos, agua, vías…). Se dibuja como overlay en el mapa
        del cliente, <strong>sin</strong> volverse predios operativos ni mezclarse con el inventario. Acepta puntos, líneas
        y polígonos.
      </p>

      <div className="panel-section" style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Nombre de la capa *</label>
          <input value={nombre} maxLength={200} onChange={(e) => setNombre(e.target.value)} placeholder="p. ej. LAAT 230 kV CFE" style={{ width: 280 }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Tipo de capa</label>
          <select value={subtipoSel} onChange={(e) => setSubtipoSel(e.target.value)}>
            {SUBTIPO_PRESETS.map((s) => <option key={s} value={s}>{s}</option>)}
            <option value={OTRO}>Otro…</option>
          </select>
          {subtipoSel === OTRO && (
            <input value={subtipoOtro} maxLength={80} onChange={(e) => setSubtipoOtro(e.target.value)} placeholder="Nombra el tipo" style={{ marginLeft: 8, width: 160 }} />
          )}
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Archivo (.kml, .kmz, .geojson)</label>
          <input
            type="file"
            accept=".kml,.kmz,.geojson,.json,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz,application/geo+json"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; void handleFile(f); }}
            disabled={parsing || submitting}
          />
          {parsing && <span style={{ marginLeft: 8 }}>Procesando…</span>}
        </div>
      </div>

      {/* Estilo */}
      <div className="panel-section" style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div>
          <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Color</label>
          <div style={{ display: 'flex', gap: 6 }}>
            {COLORES.map((c) => (
              <button key={c} type="button" onClick={() => setColor(c)} title={c}
                style={{ width: 22, height: 22, borderRadius: 4, background: c, border: color === c ? '2px solid #fff' : '1px solid rgba(0,0,0,0.3)', cursor: 'pointer', outline: color === c ? '2px solid #4A9CA5' : 'none' }} />
            ))}
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)} style={{ width: 28, height: 22, padding: 0, border: 'none', background: 'none', cursor: 'pointer' }} />
          </div>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Grosor: {grosor}</label>
          <input type="range" min={1} max={10} step={1} value={grosor} onChange={(e) => setGrosor(Number(e.target.value))} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Opacidad: {opacidad.toFixed(1)}</label>
          <input type="range" min={0.1} max={1} step={0.1} value={opacidad} onChange={(e) => setOpacidad(Number(e.target.value))} />
        </div>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
          <input type="checkbox" checked={relleno} onChange={(e) => setRelleno(e.target.checked)} /> Rellenar polígonos
        </label>
      </div>

      {parseWarnings.length > 0 && (
        <ul style={{ marginTop: 0, marginBottom: 12, color: 'var(--warn, #b56556)' }}>
          {parseWarnings.map((w, i) => <li key={i}>{w}</li>)}
        </ul>
      )}

      {items.length > 0 && (
        <div className="panel-section" style={{ marginBottom: 16 }}>
          <p style={{ margin: '0 0 8px' }}>
            <strong>{fileName}</strong> — {items.length} geometría(s):{' '}
            {Object.entries(conteoPorTipo).map(([t, n]) => `${n} ${t}`).join(', ')}
          </p>
          <button type="button" onClick={() => void submit()} disabled={submitting || !nombre.trim()}>
            {submitting ? 'Creando…' : `Crear capa de referencia (${items.length} geometría(s))`}
          </button>
        </div>
      )}

      {result && (
        <div className="panel-section">
          <h2 style={{ fontSize: 16 }}>
            Capa creada: {result.created} feature(s), {result.failed} con error
            {result.idempotent_hit ? ' (reintento — ya se había procesado)' : ''}
          </h2>
        </div>
      )}
    </div>
  );
}
